<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="page-header">
        <div>
          <p class="breadcrumb">Events → {{ eventName }}</p>
          <h1 class="page-title">Participants</h1>
          <p class="page-subtitle">Imported rows start excluded. Mark each attendee for the queue, then publish the attended list when registration is ready.</p>
        </div>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>
      <section class="panel mb-6">
        <div class="toolbar">
          <div class="toolbar-left">
            <input
              v-model.trim="searchTerm"
              type="text"
              class="search-input"
              placeholder="Search participants..."
            />
            <button class="secondary-button" @click="loadEvent">Refresh</button>
          </div>

          <div v-if="canManageParticipants" class="toolbar-right">
            <button
              class="secondary-button"
              @click="addRow('student')"
              :disabled="participantsLocked"
            >
              Add Student
            </button>
            <button
              class="secondary-button"
              @click="addRow('presenter_note')"
              :disabled="participantsLocked"
            >
              Add Presenter Note
            </button>
            <button
              class="secondary-button"
              @click="addRow('display_line')"
              :disabled="participantsLocked"
            >
              Add Display Line
            </button>
          </div>
        </div>

        <div class="toolbar meta-toolbar">
          <div>
            <div class="meta-copy">
              <span>{{ localItems.length }} rows</span>
              <span>{{ includedCount }} marked for queue</span>
              <span v-if="studentCount">{{ pronunciationSummary.ready }}/{{ studentCount }} pronunciations ready</span>
              <span v-if="participantsLocked">Locked after publish</span>
            </div>
            <div v-if="participantTypeSummaries.length" class="type-summary-row">
              <span
                v-for="summary in participantTypeSummaries"
                :key="summary.type"
                class="type-summary-pill"
              >
                {{ summary.queued }}/{{ summary.total }} {{ summary.label }} queued
              </span>
            </div>
          </div>
          <div class="toolbar-right">
            <router-link
              v-if="canExportEvent"
              :to="exportsPath"
              class="secondary-button export-link"
              title="Open event exports"
            >
              Exports
            </router-link>
            <button
              class="secondary-button"
              v-if="canGeneratePronunciations"
              @click="startPronunciationGeneration()"
              :disabled="generatingPronunciations || !pronunciationSummary.actionable"
            >
              {{ pronunciationActionLabel }}
            </button>
            <button
              class="secondary-button"
              v-if="canManageParticipants"
              @click="includeAllStudents"
              :disabled="participantsLocked || savingParticipants || !excludedStudentCount"
            >
              Include All Students
            </button>
            <button
              class="primary-button"
              v-if="canManageParticipants"
              @click="publishParticipants"
              :disabled="participantsLocked || publishingQueue || !includedCount"
            >
              {{ publishingQueue ? "Publishing..." : "Publish to Queue" }}
            </button>
          </div>
        </div>

        <p class="workflow-copy">
          Use the queue toggle as your attendance check-in. Only marked rows will be published into the live queue.
        </p>

        <p v-if="participantsLocked" class="lock-copy">
          Participants are locked because this event has already been published to the live queue.
        </p>

        <div v-if="localItems.length === 0" class="empty-panel">
          No participants yet. Import a CSV in Setup to create the source list for this event.
        </div>

        <div v-else class="participants-shell">
          <div class="column-toolbar">
            <div>
              <button
                class="secondary-button"
                type="button"
                @click="columnSettingsOpen = !columnSettingsOpen"
              >
                Columns
              </button>
              <p v-if="columnSettingsOpen" class="toolbar-hint">Saved in this browser for this event.</p>
            </div>
            <div v-if="columnSettingsOpen" class="column-actions">
              <label
                v-for="column in displayColumns"
                :key="column.key"
                class="column-chip"
                :class="{ active: isColumnVisible(column.key) }"
              >
                <input
                  type="checkbox"
                  :checked="isColumnVisible(column.key)"
                  @change="toggleColumnVisibility(column.key)"
                />
                <span>{{ column.label }}</span>
              </label>
              <button class="text-button" type="button" @click="resetVisibleColumns">
                Reset columns
              </button>
            </div>
          </div>

          <p class="scroll-copy">Scroll horizontally for extra fields. Queue and Actions stay pinned.</p>

          <div class="table-shell" :class="{ virtualized: useVirtualParticipants }">
            <div
              ref="participantsTableScroll"
              class="table-scroll"
              @scroll="onParticipantsTableScroll"
            >
              <table class="participants-table">
                <thead>
                  <tr>
                    <th class="index-column sticky-left">#</th>
                    <th class="queue-column sticky-left queue-sticky">
                      <button class="sort-button" @click="setSort('includedInQueue')">
                        Queue{{ sortIndicator('includedInQueue') }}
                      </button>
                    </th>
                    <th
                      v-for="column in visibleDisplayColumns"
                      :key="column.key"
                      :class="column.isPrimary ? 'primary-data-column' : 'data-column'"
                    >
                      <button class="sort-button" @click="setSort(column.key)">
                        {{ column.label }}{{ sortIndicator(column.key) }}
                      </button>
                    </th>
                    <th class="pronunciation-column">Pronunciation</th>
                    <th class="actions-column sticky-right">Actions</th>
                  </tr>
                </thead>

                <tbody>
                  <tr v-if="useVirtualParticipants && virtualParticipantsTopHeight" class="virtual-spacer-row">
                    <td :colspan="visibleDisplayColumns.length + 4" :style="{ height: `${virtualParticipantsTopHeight}px` }"></td>
                  </tr>

                  <template v-for="(item, index) in renderedFilteredItems" :key="item.id">
                    <tr
                      class="participant-row"
                      :class="{
                        editing: editingRowId === item.id,
                        dragging: draggingRowId === item.id,
                        'drag-over': dragOverRowId === item.id,
                      }"
                      :draggable="canReorderRows"
                      @dragstart="startRowDrag($event, item)"
                      @dragover.prevent="onRowDragOver($event, item)"
                      @drop.prevent="dropRow(item)"
                      @dragend="endRowDrag"
                    >
                      <td class="index-column sticky-left">
                        <button
                          v-if="canReorderRows"
                          class="drag-grip"
                          type="button"
                          title="Drag to reorder"
                        >
                          ⋮⋮
                        </button>
                        <span>{{ displayIndex(index) }}</span>
                      </td>
                      <td class="queue-column sticky-left queue-sticky">
                        <span
                          class="queue-badge"
                          :class="{ excluded: !item.includedInQueue }"
                          title="Queue inclusion is managed from row edit or admin actions."
                        >
                          {{ item.includedInQueue ? "Included" : "Excluded" }}
                        </span>
                      </td>
                      <td
                        v-for="column in visibleDisplayColumns"
                        :key="`${item.id}-${column.key}`"
                        :class="column.isPrimary ? 'primary-data-column' : 'data-column'"
                      >
                        <span class="cell-value truncate-cell" :title="columnValue(item, column)">
                          {{ columnValue(item, column) || "—" }}
                        </span>
                      </td>
                      <td class="pronunciation-column">
                        <div v-if="item.type === 'student'" class="pronunciation-cell">
                          <span
                            class="pronunciation-pill"
                            :class="pronunciationStatusClass(item)"
                          >
                            {{ pronunciationStatusLabel(item) }}
                          </span>
                          <span
                            v-if="collectionStatus(item) !== 'not_started'"
                            class="collection-pill"
                            :class="`collection-${collectionStatus(item)}`"
                          >
                            {{ collectionStatusLabel(item) }}
                          </span>
                          <span
                            v-if="checkStatus(item) !== 'none'"
                            class="check-pill"
                            :class="`check-${checkStatus(item)}`"
                          >
                            {{ checkStatusLabel(item) }}
                          </span>
                          <div class="pronunciation-actions">
                            <button
                              v-if="pronunciationStatus(item) === 'ready'"
                              class="icon-row-button"
                              type="button"
                              title="Play generated pronunciation"
                              @click="playPronunciation(item)"
                              :disabled="playingPronunciationId === item.id"
                            >
                              ▶
                            </button>
                            <button
                              v-if="['missing', 'failed'].includes(pronunciationStatus(item))"
                              class="text-button compact-text-button"
                              type="button"
                              @click="startPronunciationGeneration([item.id], pronunciationStatus(item) === 'failed')"
                              :disabled="generatingPronunciations"
                            >
                              Retry
                            </button>
                            <button
                              v-if="pronunciationStatus(item) === 'ready'"
                              class="text-button compact-text-button"
                              type="button"
                              @click="startPronunciationGeneration([item.id], true)"
                              :disabled="generatingPronunciations"
                            >
                              Regenerate
                            </button>
                          </div>
                        </div>
                        <span v-else class="muted-copy">—</span>
                      </td>
                      <td class="actions-column sticky-right">
                        <button
                          v-if="canManageParticipants"
                          class="row-button"
                          type="button"
                          @click="openMoveParticipantModal(item)"
                          :disabled="!canMoveParticipants"
                        >
                          Move
                        </button>
                        <button
                          v-if="item.type === 'student' && canExportEvent"
                          class="icon-row-button"
                          type="button"
                          title="Copy student review link"
                          @click="copyReviewLink(item)"
                          :disabled="copyingReviewLinkId === item.id"
                        >
                          <svg aria-hidden="true" viewBox="0 0 24 24" class="row-icon">
                            <path d="M10 13a5 5 0 0 0 7.07 0l2-2a5 5 0 0 0-7.07-7.07l-1.15 1.15" />
                            <path d="M14 11a5 5 0 0 0-7.07 0l-2 2a5 5 0 0 0 7.07 7.07l1.15-1.15" />
                          </svg>
                        </button>
                        <button
                          v-if="canEditRow(item)"
                          class="row-button"
                          @click="openEditor(item)"
                          :disabled="participantsLocked"
                        >
                          {{ editingRowId === item.id ? "Editing" : "Edit" }}
                        </button>
                      </td>
                    </tr>

                  </template>

                  <tr v-if="useVirtualParticipants && virtualParticipantsBottomHeight" class="virtual-spacer-row">
                    <td :colspan="visibleDisplayColumns.length + 4" :style="{ height: `${virtualParticipantsBottomHeight}px` }"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      <div
        v-if="draftParticipant"
        class="modal-overlay participant-editor-overlay"
      >
        <div class="participant-editor-modal" role="dialog" aria-modal="true" aria-labelledby="participant-editor-title">
          <div class="participant-editor-header">
            <div>
              <h2 id="participant-editor-title" class="panel-title">
                {{ editingRowIsNew ? "Add Participant" : "Edit Participant" }}
              </h2>
              <p class="page-subtitle">
                {{ editingRowIsNew ? "Complete the participant details, then save the row." : participantLabel(draftParticipant) }}
              </p>
            </div>
            <button class="secondary-button" type="button" :disabled="savingParticipants" @click="cancelEdit">
              Close
            </button>
          </div>

          <div class="edit-shell">
            <div class="edit-grid">
              <section class="edit-card">
                <h3 class="edit-title">Participant</h3>

                <div class="edit-form-grid">
                  <div>
                    <label class="field-label">Type</label>
                    <select v-model="draftParticipant.type" class="field-input" :disabled="!canManageParticipants">
                      <option value="student">student</option>
                      <option value="presenter_note">presenter_note</option>
                      <option value="display_line">display_line</option>
                    </select>
                  </div>

                  <label class="toggle-row">
                    <input v-model="draftParticipant.includedInQueue" type="checkbox" :disabled="!canManageParticipants" />
                    <span>Included in queue</span>
                  </label>

                  <template v-if="draftParticipant.type === 'student'">
                    <div>
                      <label class="field-label">Full Name</label>
                      <input v-model="draftParticipant.fullName" type="text" class="field-input" />
                    </div>
                    <div>
                      <label class="field-label">Award</label>
                      <input v-model="draftParticipant.award" type="text" class="field-input" :disabled="!canManageParticipants" />
                    </div>
                    <div v-if="canManageParticipants" class="full-span">
                      <label class="field-label">Orator Notes</label>
                      <input v-model="draftParticipant.oratorNotes" type="text" class="field-input" />
                    </div>
                  </template>

                  <div v-else class="full-span">
                    <label class="field-label">Text</label>
                    <input v-model="draftParticipant.text" type="text" class="field-input" />
                  </div>
                </div>

                <div
                  v-if="draftParticipant.type === 'student'"
                  class="pronunciation-detail"
                >
                  <h4 class="detail-title">Pronunciation</h4>
                  <div class="pronunciation-detail-grid">
                    <div>
                      <span class="detail-label">Status</span>
                      <span
                        class="pronunciation-pill"
                        :class="pronunciationStatusClass(draftParticipant)"
                      >
                        {{ pronunciationStatusLabel(draftParticipant) }}
                      </span>
                    </div>
                    <div v-if="draftParticipant.pronunciation?.phonetics">
                      <span class="detail-label">Phonetics</span>
                      <span class="detail-value">{{ draftParticipant.pronunciation.phonetics }}</span>
                    </div>
                    <div>
                      <label class="field-label">Corrected phonetics</label>
                      <input
                        v-model="draftParticipant.pronunciation.manualPhonetics"
                        type="text"
                        class="field-input"
                        placeholder="Type phonetics after asking the student"
                      />
                    </div>
                    <div class="check-request-copy">
                      {{
                        draftParticipant.pronunciation.checkRequested ||
                        draftParticipant.pronunciation.checkStatus === "needs_check"
                          ? "Orator Prep requested this name is checked at check-in."
                          : "No active check-in request."
                      }}
                    </div>
                    <div>
                      <label class="field-label">Check-in outcome</label>
                      <select v-model="draftParticipant.pronunciation.checkStatus" class="field-input">
                        <option value="none">None</option>
                        <option value="needs_check">Needs check</option>
                        <option value="checked_no_change">Checked, no change</option>
                        <option value="changed_at_checkin">Changed at check-in</option>
                        <option value="unable_to_confirm">Unable to confirm</option>
                      </select>
                    </div>
                    <div v-if="draftParticipant.pronunciation?.ipa">
                      <span class="detail-label">IPA</span>
                      <span class="detail-value">{{ draftParticipant.pronunciation.ipa }}</span>
                    </div>
                  </div>
                </div>

                <div class="edit-actions">
                  <button
                    v-if="canManageParticipants"
                    class="row-button"
                    @click="moveEditingRow(-1)"
                    :disabled="!canMoveEditingRow || editingRowIndex === 0"
                  >
                    Move Up
                  </button>
                  <button
                    v-if="canManageParticipants"
                    class="row-button"
                    @click="moveEditingRow(1)"
                    :disabled="!canMoveEditingRow || editingRowIndex === localItems.length - 1"
                  >
                    Move Down
                  </button>
                </div>
              </section>

              <section v-if="canManageParticipants" class="edit-card">
                <h3 class="edit-title">Imported Fields</h3>
                <p v-if="orderedExtraColumns.length === 0" class="muted-copy">No extra imported fields.</p>
                <div v-else class="extra-fields-grid">
                  <div v-for="column in orderedExtraColumns" :key="`edit-${column}`">
                    <label class="field-label">{{ column }}</label>
                    <input
                      v-model="draftParticipant.extraFields[column]"
                      type="text"
                      class="field-input"
                    />
                  </div>
                </div>
              </section>
            </div>

            <div v-if="rowSaveError" class="row-save-error" role="alert">
              <strong>Save failed. This graduate has NOT been updated.</strong>
              <span>Please try again before moving on.</span>
              <span class="row-save-error-detail">{{ rowSaveError }}</span>
            </div>

            <div class="edit-footer">
              <button
                class="primary-button"
                @click="saveRowEdits"
                :disabled="savingParticipants"
              >
                {{ savingParticipants ? "Saving..." : "Save Row" }}
              </button>
              <button class="secondary-button" :disabled="savingParticipants" @click="cancelEdit">Cancel</button>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="moveParticipant"
        class="modal-overlay"
        @click.self="closeMoveParticipantModal"
      >
        <div class="confirm-modal">
          <div class="confirm-modal-header">
            <div>
              <h2 class="panel-title">Move Participant?</h2>
              <p class="page-subtitle">
                Choose where to move <strong>{{ participantLabel(moveParticipant) }}</strong>.
                Save participants afterwards to keep this order.
              </p>
            </div>
            <button class="secondary-button" type="button" @click="closeMoveParticipantModal">
              Close
            </button>
          </div>

          <div class="modal-actions">
            <button class="secondary-button" type="button" :disabled="!canMoveParticipantUp" @click="confirmMoveParticipant('up')">
              Move Up
            </button>
            <button class="secondary-button" type="button" :disabled="!canMoveParticipantDown" @click="confirmMoveParticipant('down')">
              Move Down
            </button>
            <button class="secondary-button" type="button" :disabled="!canMoveParticipantUp" @click="confirmMoveParticipant('top')">
              Move to Top
            </button>
            <button class="primary-button" type="button" :disabled="!canMoveParticipantDown" @click="confirmMoveParticipant('bottom')">
              Send to Back
            </button>
            <button class="secondary-button" type="button" @click="closeMoveParticipantModal">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import {
  fetchEvent,
  fetchEventStudents,
  fetchStudentReviewLink,
  fetchPronunciationAudioUrl,
  fetchPronunciationJob,
  publishQueue,
  startPronunciationJob,
  updateParticipantRow,
  updateParticipants,
} from "@/lib/api";
import { createEventSocket } from "@/lib/eventSocket";
import { hasAnyRole } from "@/lib/auth";
import EventSidebar from "./EventSidebar.vue";

const PARTICIPANT_TYPES = ["student", "presenter_note", "display_line"];
const PARTICIPANT_TYPE_LABELS = {
  student: { singular: "student", plural: "students" },
  presenter_note: { singular: "presenter note", plural: "presenter notes" },
  display_line: { singular: "display line", plural: "display lines" },
};
const CORE_DISPLAY_COLUMNS = [
  { key: "fullName", label: "Full Name", isPrimary: true },
  { key: "award", label: "Award" },
  { key: "activePhonetics", label: "Phonetics" },
  { key: "checkStatus", label: "Check-in Flag" },
  { key: "type", label: "Type" },
  { key: "text", label: "Text" },
];
const PRIORITY_EXTRA_COLUMNS = [
  "student_seat_number",
  "student_seat_numbe",
  "externalid",
  "external_id",
  "prefix",
  "number",
  "email",
];
const DEFAULT_VISIBLE_CORE_COLUMNS = ["fullName", "award", "activePhonetics", "checkStatus"];
const COLUMN_STORAGE_PREFIX = "orator-pro-participants-columns-v2";
const VIRTUAL_PARTICIPANT_THRESHOLD = 150;
const VIRTUAL_PARTICIPANT_ROW_HEIGHT = 74;
const VIRTUAL_PARTICIPANT_BUFFER_ROWS = 12;

export default {
  name: "ParticipantsView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      snapshot: null,
      participantMutationEpoch: 0,
      participantRefreshSequence: 0,
      localItems: [],
      searchTerm: "",
      sortKey: "sortOrder",
      sortDirection: "asc",
      savingParticipants: false,
      publishingQueue: false,
      generatingPronunciations: false,
      pronunciationJob: null,
      pronunciationPollingTimer: null,
      columnSettingsOpen: false,
      playingPronunciationId: "",
      copyingReviewLinkId: "",
      editingRowId: "",
      editingRowIsNew: false,
      draftParticipant: null,
      rowSaveError: "",
      moveParticipantId: "",
      draggingRowId: "",
      dragOverRowId: "",
      dragScrollTimer: null,
      dragScrollStep: 0,
      ws: null,
      reconnectInterval: 1000,
      isUnmounting: false,
      visibleColumnKeys: [],
      studentCollectionStatuses: {},
      participantVirtualScrollTop: 0,
      participantVirtualViewportHeight: 640,
      error: "",
      successMessage: "",
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.snapshot?.event?.name || this.eventId;
    },
    participantsLocked() {
      return Boolean(this.snapshot?.participants?.locked);
    },
    canManageParticipants() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canCheckInParticipants() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator"]);
    },
    canExportEvent() {
      return hasAnyRole(["internal_admin", "uni_admin"]);
    },
    canGeneratePronunciations() {
      return hasAnyRole(["internal_admin", "uni_admin", "operator"]);
    },
    includedCount() {
      return this.localItems.filter((item) => item.includedInQueue).length;
    },
    studentCount() {
      return this.localItems.filter((item) => item.type === "student").length;
    },
    excludedStudentCount() {
      return this.localItems.filter(
        (item) => item.type === "student" && !item.includedInQueue
      ).length;
    },
    pronunciationSummary() {
      const summary = {
        missing: 0,
        queued: 0,
        processing: 0,
        ready: 0,
        failed: 0,
        missingPhonetics: 0,
        actionable: 0,
      };
      this.localItems
        .filter((item) => item.type === "student")
        .forEach((item) => {
          const status = this.pronunciationStatus(item);
          summary[status] = (summary[status] || 0) + 1;
          if (status === "ready" && !item.pronunciation?.phonetics) {
            summary.missingPhonetics += 1;
          }
        });
      summary.actionable = summary.missing + summary.failed + summary.missingPhonetics;
      return summary;
    },
    pronunciationActionLabel() {
      if (this.generatingPronunciations) {
        return "Generating...";
      }
      if (!this.studentCount) {
        return "No students";
      }
      if (this.pronunciationSummary.failed) {
        return `Retry ${this.pronunciationSummary.failed} failed`;
      }
      if (this.pronunciationSummary.missing) {
        return `Generate ${this.pronunciationSummary.missing} pronunciations`;
      }
      if (this.pronunciationSummary.missingPhonetics) {
        return `Add phonetics to ${this.pronunciationSummary.missingPhonetics}`;
      }
      return "Pronunciations ready";
    },
    participantTypeSummaries() {
      return PARTICIPANT_TYPES
        .map((type) => {
          const items = this.localItems.filter((item) => item.type === type);
          if (!items.length) {
            return null;
          }
          return {
            type,
            total: items.length,
            queued: items.filter((item) => item.includedInQueue).length,
            label: this.participantTypeLabel(type, items.length),
          };
        })
        .filter(Boolean);
    },
    allExtraColumns() {
      const snapshotColumns = this.snapshot?.participants?.columns || [];
      const seen = new Set(snapshotColumns);
      const columns = [...snapshotColumns];
      this.localItems.forEach((item) => {
        Object.keys(item.extraFields || {}).forEach((column) => {
          if (!seen.has(column)) {
            seen.add(column);
            columns.push(column);
          }
        });
      });
      return columns;
    },
    orderedExtraColumns() {
      const priorityMap = new Map(PRIORITY_EXTRA_COLUMNS.map((column, index) => [column, index]));
      return [...this.allExtraColumns].sort((left, right) => {
        const leftPriority = priorityMap.has(left.toLowerCase()) ? priorityMap.get(left.toLowerCase()) : 999;
        const rightPriority = priorityMap.has(right.toLowerCase()) ? priorityMap.get(right.toLowerCase()) : 999;
        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }
        return left.localeCompare(right);
      });
    },
    displayColumns() {
      const extraColumns = this.orderedExtraColumns.map((column) => ({
        key: column,
        label: column,
        isExtra: true,
      }));
      return [...CORE_DISPLAY_COLUMNS, ...extraColumns];
    },
    visibleDisplayColumns() {
      const allowedKeys = new Set(this.visibleColumnKeys.length ? this.visibleColumnKeys : this.defaultVisibleColumnKeys());
      const visibleColumns = this.displayColumns.filter((column) => allowedKeys.has(column.key));
      return visibleColumns.length ? visibleColumns : this.displayColumns;
    },
    filteredItems() {
      const query = this.searchTerm.trim().toLowerCase();
      const items = this.localItems.filter((item) => this.matchesSearch(item, query));
      return [...items].sort((left, right) => this.compareItems(left, right));
    },
    useVirtualParticipants() {
      return this.filteredItems.length > VIRTUAL_PARTICIPANT_THRESHOLD;
    },
    virtualParticipantStartIndex() {
      if (!this.useVirtualParticipants) {
        return 0;
      }
      const startIndex = Math.floor(this.participantVirtualScrollTop / VIRTUAL_PARTICIPANT_ROW_HEIGHT);
      return Math.max(startIndex - VIRTUAL_PARTICIPANT_BUFFER_ROWS, 0);
    },
    virtualParticipantEndIndex() {
      if (!this.useVirtualParticipants) {
        return this.filteredItems.length;
      }
      const visibleRows = Math.ceil(this.participantVirtualViewportHeight / VIRTUAL_PARTICIPANT_ROW_HEIGHT);
      return Math.min(
        this.virtualParticipantStartIndex + visibleRows + VIRTUAL_PARTICIPANT_BUFFER_ROWS * 2,
        this.filteredItems.length
      );
    },
    renderedFilteredItems() {
      if (!this.useVirtualParticipants) {
        return this.filteredItems;
      }
      return this.filteredItems.slice(this.virtualParticipantStartIndex, this.virtualParticipantEndIndex);
    },
    virtualParticipantsTopHeight() {
      return this.virtualParticipantStartIndex * VIRTUAL_PARTICIPANT_ROW_HEIGHT;
    },
    virtualParticipantsBottomHeight() {
      const remainingRows = this.filteredItems.length - this.virtualParticipantEndIndex;
      return Math.max(remainingRows, 0) * VIRTUAL_PARTICIPANT_ROW_HEIGHT;
    },
    editingRowIndex() {
      return this.localItems.findIndex((item) => item.id === this.editingRowId);
    },
    canReorderRows() {
      return this.canMoveEditingRow && !this.useVirtualParticipants;
    },
    canMoveParticipants() {
      return (
        this.canManageParticipants &&
        !this.participantsLocked &&
        !this.searchTerm.trim() &&
        this.sortKey === "sortOrder"
      );
    },
    canMoveEditingRow() {
      return this.canMoveParticipants && !this.editingRowIsNew;
    },
    moveParticipant() {
      if (!this.moveParticipantId) {
        return null;
      }
      return this.localItems.find((item) => item.id === this.moveParticipantId) || null;
    },
    moveParticipantIndex() {
      if (!this.moveParticipantId) {
        return -1;
      }
      return this.localItems.findIndex((item) => item.id === this.moveParticipantId);
    },
    canMoveParticipantUp() {
      return this.canMoveParticipants && this.moveParticipantIndex > 0;
    },
    canMoveParticipantDown() {
      const index = this.moveParticipantIndex;
      return this.canMoveParticipants && index >= 0 && index < this.localItems.length - 1;
    },
    queuePath() {
      return `/events/${encodeURIComponent(this.eventId)}/queue`;
    },
    exportsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/exports`;
    },
  },
  watch: {
    searchTerm() {
      this.resetParticipantVirtualScroll();
    },
    sortKey() {
      this.resetParticipantVirtualScroll();
    },
    sortDirection() {
      this.resetParticipantVirtualScroll();
    },
  },
  methods: {
    canEditRow(item) {
      if (this.canManageParticipants) {
        return true;
      }
      return this.canCheckInParticipants && item?.type === "student";
    },
    participantTypeLabel(type, count) {
      const labels = PARTICIPANT_TYPE_LABELS[type];
      if (!labels) {
        const readableType = type.replace(/_/g, " ");
        return count === 1 ? readableType : `${readableType}s`;
      }
      return count === 1 ? labels.singular : labels.plural;
    },
    async loadEvent() {
      this.error = "";
      this.successMessage = "";
      try {
        const snapshot = await fetchEvent(this.eventId);
        this.applySnapshot(snapshot);
        await this.loadStudentCollectionStatuses();
      } catch (error) {
        this.error = "Unable to load participants for this event.";
      }
    },
    async loadStudentCollectionStatuses() {
      try {
        const roster = await fetchEventStudents(this.eventId);
        this.studentCollectionStatuses = (roster.students || []).reduce((acc, student) => {
          acc[student.studentId] = student.collection?.status || "not_started";
          return acc;
        }, {});
      } catch (error) {
        this.studentCollectionStatuses = {};
      }
    },
    applySnapshot(snapshot) {
      this.snapshot = snapshot;
      this.localItems = (snapshot?.participants?.items || []).map((item) => this.cloneParticipant(item));
      this.syncVisibleColumns();
      this.editingRowId = "";
      this.editingRowIsNew = false;
      this.draftParticipant = null;
    },
    cloneParticipant(item) {
      return {
        ...item,
        extraFields: { ...(item.extraFields || {}) },
        pronunciation: this.normalizeDraftPronunciation(item.pronunciation),
      };
    },
    normalizeDraftPronunciation(pronunciation = {}) {
      return {
        ...(pronunciation || {}),
        manualPhonetics: pronunciation?.manualPhonetics || "",
        checkRequested: Boolean(pronunciation?.checkRequested),
        checkStatus: pronunciation?.checkStatus || "none",
      };
    },
    participantSignature(item) {
      const normalizedItem = this.cloneParticipant(item || {});
      const extraFields = Object.entries(normalizedItem.extraFields || {})
        .sort(([left], [right]) => left.localeCompare(right))
        .reduce((acc, [key, value]) => {
          acc[key] = value;
          return acc;
        }, {});
      return JSON.stringify({
        id: normalizedItem.id || "",
        type: normalizedItem.type || "",
        fullName: normalizedItem.fullName || "",
        award: normalizedItem.award || "",
        text: normalizedItem.text || "",
        oratorNotes: normalizedItem.oratorNotes || "",
        includedInQueue: Boolean(normalizedItem.includedInQueue),
        sortOrder: Number(normalizedItem.sortOrder || 0),
        extraFields,
        pronunciation: normalizedItem.pronunciation || {},
      });
    },
    mergePronunciationUpdate(incomingParticipants) {
      const latestById = new Map(
        (incomingParticipants?.items || []).map((item) => [item.id, item])
      );
      const mergeItem = (item) => {
        const latestItem = latestById.get(item.id);
        if (!latestItem) {
          return item;
        }
        return this.cloneParticipant({
          ...item,
          pronunciation: this.normalizeDraftPronunciation(latestItem.pronunciation),
        });
      };
      if (this.draftParticipant?.id) {
        const baseline = (this.snapshot?.participants?.items || [])
          .find((item) => item.id === this.draftParticipant.id)?.pronunciation || {};
        const baselinePronunciation = this.normalizeDraftPronunciation(baseline);
        const draftPronunciation = this.draftParticipant.pronunciation || {};
        const refreshedDraft = mergeItem(this.draftParticipant);
        // Keep only fields edited locally. A clean editor adopts remote check-in
        // corrections instead of submitting stale values over another operator.
        for (const key of ["manualPhonetics", "checkRequested", "checkStatus"]) {
          if (draftPronunciation[key] !== baselinePronunciation[key]) {
            refreshedDraft.pronunciation[key] = draftPronunciation[key];
          }
        }
        this.draftParticipant = refreshedDraft;
      }
      this.localItems = this.localItems.map(mergeItem);
      if (this.snapshot?.participants) {
        this.snapshot = {
          ...this.snapshot,
          participants: {
            ...this.snapshot.participants,
            ...incomingParticipants,
            items: (this.snapshot.participants.items || []).map(mergeItem),
          },
        };
      }
    },
    hasUnsavedParticipantChanges() {
      if (this.editingRowId || this.draftParticipant) {
        return true;
      }
      const snapshotItems = this.snapshot?.participants?.items || [];
      if (snapshotItems.length !== this.localItems.length) {
        return true;
      }
      return this.localItems.some(
        (item, index) => this.participantSignature(item) !== this.participantSignature(snapshotItems[index] || {})
      );
    },
    withReindexedSortOrder(items) {
      return items.map((item, index) => ({
        ...item,
        sortOrder: index,
      }));
    },
    displayIndex(index) {
      return (this.useVirtualParticipants ? this.virtualParticipantStartIndex : 0) + index + 1;
    },
    participantLabel(item) {
      return item?.fullName || item?.text || item?.id || "this row";
    },
    connectWebSocket() {
      this.ws = createEventSocket(this.eventId, {
        onMessage: async (data) => {
          if (data.type !== "refresh") {
            return;
          }
          if (this.savingParticipants || this.publishingQueue) {
            return;
          }
          const mutationEpoch = this.participantMutationEpoch;
          const refreshSequence = ++this.participantRefreshSequence;
          let latestSnapshot = null;
          try {
            latestSnapshot = await fetchEvent(this.eventId);
          } catch (error) {
            return;
          }
          // Saves may finish while this request is in flight. A response from
          // before that mutation, or before a newer refresh, cannot replace it.
          if (
            this.savingParticipants || this.publishingQueue ||
            mutationEpoch !== this.participantMutationEpoch ||
            refreshSequence !== this.participantRefreshSequence
          ) {
            return;
          }
          if (this.hasUnsavedParticipantChanges()) {
            this.mergePronunciationUpdate(latestSnapshot.participants);
            await this.loadStudentCollectionStatuses();
            return;
          }
          this.applySnapshot(latestSnapshot);
          await this.loadStudentCollectionStatuses();
        },
        onClose: () => {
          if (this.isUnmounting) {
            return;
          }
          window.setTimeout(() => {
            this.reconnectInterval = Math.min(this.reconnectInterval * 2, 30000);
            this.connectWebSocket();
          }, this.reconnectInterval);
        },
      });
    },
    onParticipantsTableScroll(event) {
      this.participantVirtualScrollTop = event.target.scrollTop || 0;
      this.updateParticipantVirtualViewport();
    },
    updateParticipantVirtualViewport() {
      const scroller = this.$refs.participantsTableScroll;
      if (!scroller) {
        return;
      }
      this.participantVirtualViewportHeight = scroller.clientHeight || this.participantVirtualViewportHeight;
      this.participantVirtualScrollTop = scroller.scrollTop || 0;
    },
    resetParticipantVirtualScroll() {
      this.participantVirtualScrollTop = 0;
      this.$nextTick(() => {
        const scroller = this.$refs.participantsTableScroll;
        if (scroller) {
          scroller.scrollTop = 0;
        }
        this.updateParticipantVirtualViewport();
      });
    },
    columnStorageKey() {
      return `${COLUMN_STORAGE_PREFIX}:${this.eventId}`;
    },
    availableColumnKeys() {
      return this.displayColumns.map((column) => column.key);
    },
    sanitizeVisibleColumns(columnKeys) {
      const allowedKeys = new Set(this.availableColumnKeys());
      const normalizedKeys = Array.isArray(columnKeys) ? columnKeys : [];
      return normalizedKeys.filter((key, index, items) => allowedKeys.has(key) && items.indexOf(key) === index);
    },
    defaultVisibleColumnKeys() {
      const visibleKeys = [...DEFAULT_VISIBLE_CORE_COLUMNS];
      const prioritySet = new Set(PRIORITY_EXTRA_COLUMNS.map((column) => column.toLowerCase()));
      const prioritizedExtraColumns = this.orderedExtraColumns.filter((column) => prioritySet.has(column.toLowerCase()));
      prioritizedExtraColumns.slice(0, 2).forEach((column) => {
        visibleKeys.push(column);
      });
      return this.sanitizeVisibleColumns(visibleKeys);
    },
    readStoredVisibleColumns() {
      if (typeof window === "undefined") {
        return null;
      }
      try {
        const storedValue = window.localStorage.getItem(this.columnStorageKey());
        return storedValue ? JSON.parse(storedValue) : null;
      } catch (error) {
        return null;
      }
    },
    persistVisibleColumns() {
      if (typeof window === "undefined") {
        return;
      }
      try {
        window.localStorage.setItem(
          this.columnStorageKey(),
          JSON.stringify(this.visibleColumnKeys)
        );
      } catch (error) {
        // Ignore storage failures and keep the in-memory state.
      }
    },
    syncVisibleColumns() {
      const storedColumns = this.readStoredVisibleColumns();
      const nextVisibleColumns = this.sanitizeVisibleColumns(
        this.visibleColumnKeys.length ? this.visibleColumnKeys : storedColumns
      );
      this.visibleColumnKeys = nextVisibleColumns.length
        ? nextVisibleColumns
        : this.defaultVisibleColumnKeys();
      this.persistVisibleColumns();
    },
    isColumnVisible(columnKey) {
      return this.visibleDisplayColumns.some((column) => column.key === columnKey);
    },
    toggleColumnVisibility(columnKey) {
      const currentVisibleColumns = this.visibleDisplayColumns.map((column) => column.key);
      const isVisible = this.isColumnVisible(columnKey);
      if (isVisible && currentVisibleColumns.length === 1) {
        return;
      }

      const nextVisibleColumns = isVisible
        ? currentVisibleColumns.filter((key) => key !== columnKey)
        : [...currentVisibleColumns, columnKey];
      this.visibleColumnKeys = this.sanitizeVisibleColumns(nextVisibleColumns);
      if (!this.visibleColumnKeys.length) {
        this.visibleColumnKeys = this.defaultVisibleColumnKeys();
      }
      this.persistVisibleColumns();
    },
    resetVisibleColumns() {
      this.visibleColumnKeys = this.defaultVisibleColumnKeys();
      this.persistVisibleColumns();
    },
    columnValue(item, column) {
      if (!item || !column) {
        return "";
      }
      if (column.isExtra) {
        return this.extraFieldValue(item, column.key);
      }
      if (column.key === "activePhonetics") {
        return this.activePhonetics(item);
      }
      if (column.key === "checkStatus") {
        return this.checkStatusLabel(item);
      }
      return item[column.key] || "";
    },
    extraFieldValue(item, column) {
      return item.extraFields?.[column] || "";
    },
    matchesSearch(item, query) {
      if (!query) {
        return true;
      }
      const haystack = [
        item.type,
        item.fullName,
        item.award,
        item.text,
        item.includedInQueue ? "included" : "excluded",
        item.pronunciation?.status,
        this.activePhonetics(item),
        item.pronunciation?.phonetics,
        item.pronunciation?.ipa,
        this.checkStatusLabel(item),
        ...Object.values(item.extraFields || {}),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    },
    compareItems(left, right) {
      const direction = this.sortDirection === "asc" ? 1 : -1;
      const leftValue = this.sortValue(left, this.sortKey);
      const rightValue = this.sortValue(right, this.sortKey);
      if (leftValue < rightValue) {
        return -1 * direction;
      }
      if (leftValue > rightValue) {
        return 1 * direction;
      }
      return (left.sortOrder || 0) - (right.sortOrder || 0);
    },
    sortValue(item, key) {
      if (key === "includedInQueue") {
        return item.includedInQueue ? "1" : "0";
      }
      if (key === "sortOrder") {
        return String(item.sortOrder || 0).padStart(6, "0");
      }
      if (key === "activePhonetics") {
        return this.activePhonetics(item).toLowerCase();
      }
      if (key === "checkStatus") {
        return this.checkStatusLabel(item).toLowerCase();
      }
      if (["type", "fullName", "award", "text"].includes(key)) {
        return (item[key] || "").toString().toLowerCase();
      }
      if (key === "pronunciation") {
        return this.pronunciationStatus(item);
      }
      return ((item.extraFields || {})[key] || "").toString().toLowerCase();
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
        return;
      }
      this.sortKey = key;
      this.sortDirection = "asc";
    },
    sortIndicator(key) {
      if (this.sortKey !== key) {
        return "";
      }
      return this.sortDirection === "asc" ? " ↑" : " ↓";
    },
    pronunciationStatus(item) {
      if (!item?.pronunciation?.status) {
        return "missing";
      }
      const status = item.pronunciation.status;
      return ["queued", "processing", "ready", "failed"].includes(status) ? status : "missing";
    },
    pronunciationStatusLabel(item) {
      const labels = {
        missing: "Missing",
        queued: "Queued",
        processing: "Processing",
        ready: "Ready",
        failed: "Failed",
      };
      return labels[this.pronunciationStatus(item)] || "Missing";
    },
    pronunciationStatusClass(item) {
      return `status-${this.pronunciationStatus(item)}`;
    },
    activePhonetics(item) {
      const pronunciation = item?.pronunciation || {};
      return (
        pronunciation.manualPhonetics ||
        pronunciation.activePhonetics ||
        pronunciation.phonetics ||
        pronunciation.generated?.phonetics ||
        ""
      );
    },
    checkStatus(item) {
      const status = item?.pronunciation?.checkStatus || "none";
      return [
        "needs_check",
        "checked_no_change",
        "changed_at_checkin",
        "unable_to_confirm",
      ].includes(status)
        ? status
        : "none";
    },
    checkStatusLabel(itemOrStatus) {
      const status =
        typeof itemOrStatus === "string" ? itemOrStatus : this.checkStatus(itemOrStatus);
      const labels = {
        none: "No check",
        needs_check: "Ask at check-in",
        checked_no_change: "Checked",
        changed_at_checkin: "Changed at check-in",
        unable_to_confirm: "Unable to confirm",
      };
      return labels[status] || "No check";
    },
    collectionStatus(item) {
      return this.studentCollectionStatuses[item?.id] || "not_started";
    },
    collectionStatusLabel(item) {
      const labels = {
        invited: "Invited",
        confirmed_generated: "Confirmed",
        selected_alternative: "Alternative",
        needs_review: "Needs review",
        approved: "Approved",
        rejected: "Rejected",
        closed: "Closed",
      };
      return labels[this.collectionStatus(item)] || "Not started";
    },
    clearPronunciationPolling() {
      if (this.pronunciationPollingTimer) {
        window.clearTimeout(this.pronunciationPollingTimer);
        this.pronunciationPollingTimer = null;
      }
    },
    schedulePronunciationPolling(jobId) {
      this.clearPronunciationPolling();
      this.pronunciationPollingTimer = window.setTimeout(() => {
        this.pollPronunciationJob(jobId);
      }, 2000);
    },
    async pollPronunciationJob(jobId) {
      try {
        const job = await fetchPronunciationJob(this.eventId, jobId);
        this.pronunciationJob = job;
        if (["completed", "partial_failed", "failed"].includes(job.status)) {
          this.clearPronunciationPolling();
          await this.loadEvent();
          return;
        }
        this.schedulePronunciationPolling(jobId);
      } catch (error) {
        this.clearPronunciationPolling();
      }
    },
    async startPronunciationGeneration(participantIds = null, force = false) {
      this.generatingPronunciations = true;
      this.error = "";
      this.successMessage = "";
      try {
        const payload = {
          force,
        };
        if (participantIds?.length) {
          payload.participantIds = participantIds;
        }
        const result = await startPronunciationJob(this.eventId, payload);
        this.pronunciationJob = result.job;
        if (result.participants && this.snapshot) {
          this.applySnapshot({
            ...this.snapshot,
            participants: result.participants,
          });
        }
        const queued = result.job?.queued || 0;
        const skipped = result.job?.skipped || 0;
        this.successMessage = queued
          ? `Pronunciation generation queued for ${queued} students${skipped ? `; ${skipped} already ready` : ""}.`
          : "No missing pronunciations to generate.";
        if (queued && result.job?.jobId) {
          this.schedulePronunciationPolling(result.job.jobId);
        }
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to start pronunciation generation.";
      } finally {
        this.generatingPronunciations = false;
      }
    },
    async playPronunciation(item) {
      if (!item?.id || this.pronunciationStatus(item) !== "ready") {
        return;
      }
      this.playingPronunciationId = item.id;
      this.error = "";
      try {
        const { url } = await fetchPronunciationAudioUrl(this.eventId, item.id);
        const audio = new Audio(url);
        await audio.play();
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to play pronunciation audio.";
      } finally {
        this.playingPronunciationId = "";
      }
    },
    async copyReviewLink(item) {
      if (!item?.id || item.type !== "student") {
        return;
      }
      this.copyingReviewLinkId = item.id;
      this.error = "";
      this.successMessage = "";
      try {
        const url = await fetchStudentReviewLink(this.eventId, item.id);
        const absoluteUrl = url.startsWith("http")
          ? url
          : `${window.location.origin}${url}`;
        await navigator.clipboard.writeText(absoluteUrl);
        this.successMessage = "Review link copied.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to copy review link.";
      } finally {
        this.copyingReviewLinkId = "";
      }
    },
    includeAllStudents() {
      this.localItems = this.localItems.map((item) =>
        item.type === "student"
          ? {
              ...item,
              includedInQueue: true,
            }
          : item
      );
      if (this.draftParticipant?.type === "student") {
        this.draftParticipant.includedInQueue = true;
      }
      this.successMessage = `${this.studentCount} students marked for queue. Save participants before publishing.`;
      this.error = "";
    },
    openEditor(item) {
      if (this.participantsLocked) {
        return;
      }
      if (this.draftParticipant && this.editingRowId === item.id) {
        return;
      }
      if (
        this.draftParticipant &&
        !window.confirm("Discard the current unsaved participant changes?")
      ) {
        return;
      }
      if (this.draftParticipant) {
        this.cancelEdit();
      }
      this.editingRowId = item.id;
      this.editingRowIsNew = false;
      this.draftParticipant = this.cloneParticipant(item);
      this.draftParticipant.pronunciation = this.normalizeDraftPronunciation(
        this.draftParticipant.pronunciation
      );
      this.successMessage = "";
      this.error = "";
      this.rowSaveError = "";
    },
    cancelEdit() {
      if (this.editingRowIsNew && this.editingRowId) {
        this.localItems = this.localItems.filter((item) => item.id !== this.editingRowId);
      }
      this.editingRowId = "";
      this.editingRowIsNew = false;
      this.draftParticipant = null;
      this.rowSaveError = "";
    },
    addRow(type) {
      if (
        this.draftParticipant &&
        !window.confirm("Discard the current unsaved participant changes?")
      ) {
        return;
      }
      if (this.draftParticipant) {
        this.cancelEdit();
      }
      const itemType = PARTICIPANT_TYPES.includes(type) ? type : "student";
      const newItem = {
        id: `participant-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: itemType,
        fullName: "",
        award: "",
        text: "",
        extraFields: {},
        oratorNotes: "",
        pronunciation: this.normalizeDraftPronunciation(),
        includedInQueue: false,
        sortOrder: this.localItems.length,
      };
      this.editingRowId = newItem.id;
      this.editingRowIsNew = true;
      this.draftParticipant = this.cloneParticipant(newItem);
      this.draggingRowId = "";
      this.dragOverRowId = "";
      this.successMessage = "";
      this.error = "";
      this.rowSaveError = "";
    },
    startRowDrag(event, item) {
      if (!this.canReorderRows) {
        return;
      }
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.dropEffect = "move";
        event.dataTransfer.setData("text/plain", item.id);
      }
      this.draggingRowId = item.id;
      this.dragOverRowId = "";
    },
    onRowDragOver(event, item) {
      if (!this.canReorderRows || !this.draggingRowId || this.draggingRowId === item.id) {
        this.stopAutoScroll();
        return;
      }
      this.dragOverRowId = item.id;
      this.updateAutoScroll(event);
    },
    dropRow(targetItem) {
      if (!this.canReorderRows || !this.draggingRowId || this.draggingRowId === targetItem.id) {
        this.endRowDrag();
        return;
      }

      const currentIndex = this.localItems.findIndex((item) => item.id === this.draggingRowId);
      const targetIndex = this.localItems.findIndex((item) => item.id === targetItem.id);
      if (currentIndex < 0 || targetIndex < 0) {
        this.endRowDrag();
        return;
      }

      const nextItems = [...this.localItems];
      const [movedItem] = nextItems.splice(currentIndex, 1);
      nextItems.splice(targetIndex, 0, movedItem);
      this.localItems = this.withReindexedSortOrder(nextItems);
      this.dragOverRowId = "";
      this.draggingRowId = "";
      this.stopAutoScroll();
    },
    endRowDrag() {
      this.draggingRowId = "";
      this.dragOverRowId = "";
      this.stopAutoScroll();
    },
    updateAutoScroll(event) {
      const clientY = event?.clientY;
      if (typeof clientY !== "number") {
        this.stopAutoScroll();
        return;
      }

      const edgeThreshold = 90;
      const maxStep = 24;
      let scrollStep = 0;

      if (clientY < edgeThreshold) {
        const distance = edgeThreshold - clientY;
        scrollStep = -Math.max(6, Math.min(maxStep, Math.ceil(distance / 4)));
      } else if (clientY > window.innerHeight - edgeThreshold) {
        const distance = clientY - (window.innerHeight - edgeThreshold);
        scrollStep = Math.max(6, Math.min(maxStep, Math.ceil(distance / 4)));
      }

      if (!scrollStep) {
        this.stopAutoScroll();
        return;
      }

      this.dragScrollStep = scrollStep;
      if (this.dragScrollTimer) {
        return;
      }

      this.dragScrollTimer = window.setInterval(() => {
        window.scrollBy(0, this.dragScrollStep);
      }, 16);
    },
    stopAutoScroll() {
      this.dragScrollStep = 0;
      if (this.dragScrollTimer) {
        window.clearInterval(this.dragScrollTimer);
        this.dragScrollTimer = null;
      }
    },
    moveEditingRow(direction) {
      const currentIndex = this.editingRowIndex;
      const nextIndex = currentIndex + direction;
      if (currentIndex < 0 || nextIndex < 0 || nextIndex >= this.localItems.length) {
        return;
      }
      const nextItems = [...this.localItems];
      const [item] = nextItems.splice(currentIndex, 1);
      nextItems.splice(nextIndex, 0, item);
      this.localItems = this.withReindexedSortOrder(nextItems);
    },
    openMoveParticipantModal(item) {
      if (!this.canMoveParticipants || !item?.id) {
        return;
      }
      this.moveParticipantId = item.id;
    },
    closeMoveParticipantModal() {
      this.moveParticipantId = "";
    },
    confirmMoveParticipant(action) {
      if (!this.moveParticipant) {
        this.closeMoveParticipantModal();
        return;
      }
      const currentIndex = this.moveParticipantIndex;
      const lastIndex = this.localItems.length - 1;
      const targetIndexByAction = {
        up: currentIndex - 1,
        down: currentIndex + 1,
        top: 0,
        bottom: lastIndex,
      };
      const targetIndex = targetIndexByAction[action];
      if (
        currentIndex < 0 ||
        targetIndex === undefined ||
        targetIndex < 0 ||
        targetIndex > lastIndex ||
        targetIndex === currentIndex
      ) {
        this.closeMoveParticipantModal();
        return;
      }
      const nextItems = [...this.localItems];
      const [item] = nextItems.splice(currentIndex, 1);
      nextItems.splice(targetIndex, 0, item);
      this.localItems = this.withReindexedSortOrder(nextItems);
      this.successMessage = "Participant order updated. Save participants to keep this order.";
      this.error = "";
      this.closeMoveParticipantModal();
    },
    applyDraftToLocalItems() {
      if (
        this.editingRowIsNew &&
        !this.localItems.some((item) => item.id === this.draftParticipant.id)
      ) {
        this.localItems = [
          ...this.localItems,
          this.cloneParticipant(this.draftParticipant),
        ];
        return;
      }
      const nextItems = this.localItems.map((item) =>
        item.id === this.draftParticipant.id ? this.cloneParticipant(this.draftParticipant) : item
      );
      this.localItems = nextItems;
    },
    replaceParticipant(updatedParticipant, updatedParticipantsState = null) {
      if (updatedParticipantsState?.items) {
        this.localItems = (updatedParticipantsState.items || []).map((item) => this.cloneParticipant(item));
      } else {
        this.localItems = this.localItems.map((item) =>
          item.id === updatedParticipant.id ? this.cloneParticipant(updatedParticipant) : item
        );
      }
      if (this.snapshot?.participants) {
        this.snapshot = {
          ...this.snapshot,
          participants: {
            ...this.snapshot.participants,
            ...(updatedParticipantsState || {}),
            items: this.localItems.map((item) => this.cloneParticipant(item)),
            columns: updatedParticipantsState?.columns || this.allExtraColumns,
          },
        };
      }
    },
    hasPendingOrderChanges() {
      const snapshotItems = this.snapshot?.participants?.items || [];
      if (snapshotItems.length !== this.localItems.length) {
        return true;
      }
      return this.localItems.some((item, index) => item.id !== snapshotItems[index]?.id);
    },
    buildPayload() {
      return {
        items: this.localItems.map((item, index) => ({
          ...item,
          extraFields: { ...(item.extraFields || {}) },
          sortOrder: index,
        })),
        columns: this.allExtraColumns,
        locked: this.snapshot?.participants?.locked || false,
        publishedToQueueAt: this.snapshot?.participants?.publishedToQueueAt || "",
      };
    },
    async persistParticipants(successMessage) {
      const snapshot = await updateParticipants(this.eventId, this.buildPayload());
      this.applySnapshot(snapshot);
      this.successMessage = successMessage;
    },
    async saveParticipantsList() {
      this.savingParticipants = true;
      this.participantMutationEpoch += 1;
      this.error = "";
      this.successMessage = "";
      this.rowSaveError = "";
      try {
        if (this.draftParticipant) {
          this.applyDraftToLocalItems();
        }
        await this.persistParticipants("Participants saved.");
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to save participants.";
      } finally {
        this.savingParticipants = false;
      }
    },
    async saveRowEdits() {
      if (!this.draftParticipant) {
        return;
      }
      this.savingParticipants = true;
      this.participantMutationEpoch += 1;
      this.error = "";
      this.successMessage = "";
      this.rowSaveError = "";
      try {
        if (this.editingRowIsNew || this.hasPendingOrderChanges()) {
          this.applyDraftToLocalItems();
          await this.persistParticipants("Participant row saved.");
        } else {
          const response = await updateParticipantRow(
            this.eventId,
            this.draftParticipant.id,
            this.buildRowEditPayload()
          );
          this.replaceParticipant(response.participant, response.participants);
          this.editingRowId = "";
          this.editingRowIsNew = false;
          this.draftParticipant = null;
          this.rowSaveError = "";
          this.successMessage = "Participant row saved.";
        }
      } catch (error) {
        const message = error?.response?.data?.error || "Unable to save participant row.";
        this.error = message;
        this.rowSaveError = message;
      } finally {
        this.savingParticipants = false;
      }
    },
    buildRowEditPayload() {
      const pronunciation = this.normalizedCheckinPronunciation();
      if (!this.canManageParticipants) {
        return {
          fullName: this.draftParticipant.fullName,
          pronunciation: {
            manualPhonetics: pronunciation.manualPhonetics,
            checkRequested: pronunciation.checkRequested,
            checkStatus: pronunciation.checkStatus,
          },
        };
      }
      return {
        type: this.draftParticipant.type,
        fullName: this.draftParticipant.fullName,
        award: this.draftParticipant.award,
        text: this.draftParticipant.text,
        oratorNotes: this.draftParticipant.oratorNotes,
        extraFields: { ...(this.draftParticipant.extraFields || {}) },
        includedInQueue: this.draftParticipant.includedInQueue,
        pronunciation,
      };
    },
    normalizedCheckinPronunciation() {
      const pronunciation = this.draftParticipant?.pronunciation || {};
      const checkStatus = pronunciation.checkStatus || "none";
      return {
        ...pronunciation,
        manualPhonetics: pronunciation.manualPhonetics || "",
        checkRequested: checkStatus === "needs_check",
        checkStatus,
      };
    },
    async publishParticipants() {
      if (!window.confirm("Publish the marked participant rows into the live queue and lock this participant list?")) {
        return;
      }

      this.publishingQueue = true;
      this.participantMutationEpoch += 1;
      this.error = "";
      this.successMessage = "";
      try {
        await updateParticipants(this.eventId, this.buildPayload());
        const snapshot = await publishQueue(this.eventId, { baseRevision: this.snapshot?.queue?.revision });
        this.applySnapshot(snapshot);
        this.successMessage = "Queue published. Continue in Queue to run the event.";
        this.$router.push(this.queuePath);
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to publish the queue.";
      } finally {
        this.publishingQueue = false;
      }
    },
  },
  async mounted() {
    await this.loadEvent();
    window.addEventListener("resize", this.updateParticipantVirtualViewport);
    this.$nextTick(() => this.updateParticipantVirtualViewport());
    this.connectWebSocket();
  },
  beforeUnmount() {
    this.isUnmounting = true;
    window.removeEventListener("resize", this.updateParticipantVirtualViewport);
    if (this.ws) {
      this.ws.close();
    }
    this.stopAutoScroll();
    this.clearPronunciationPolling();
  },
};
</script>

<style scoped>
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 0.125rem 2.5rem;
}

.event-main {
  flex: 1 1 auto;
  min-width: 0;
}

:deep(.sidebar) {
  width: 168px;
  flex-basis: 168px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  margin-bottom: 1.5rem;
}

.breadcrumb {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--orator-graphite);
}

.page-title {
  margin-top: 0.35rem;
  font-size: 2rem;
  font-weight: 900;
  color: var(--orator-navy);
}

.page-subtitle {
  margin-top: 0.35rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.header-actions,
.toolbar-left,
.toolbar-right,
.edit-actions,
.edit-footer {
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-wrap: wrap;
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px 0 rgb(0 0 0 / 0.06);
  padding: 1.25rem;
}

.toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.meta-toolbar {
  margin-top: 1rem;
}

.meta-copy {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  color: var(--orator-graphite);
  font-size: 0.95rem;
}

.type-summary-row {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
}

.type-summary-pill {
  display: inline-flex;
  align-items: center;
  border-radius: 999px;
  padding: 0.42rem 0.75rem;
  background: #f0f9fb;
  color: #0f4f5d;
  border: 1px solid #b6e0e7;
  font-size: 0.9rem;
  font-weight: 700;
}

.workflow-copy,
.scroll-copy {
  margin-top: 1rem;
  color: var(--orator-graphite);
  font-weight: 500;
}

.participants-shell {
  margin-top: 1rem;
}

.column-toolbar {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: flex-start;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.toolbar-hint {
  margin-top: 0.3rem;
  color: var(--orator-graphite);
  font-size: 0.92rem;
}

.column-actions {
  display: flex;
  gap: 0.6rem;
  flex-wrap: wrap;
  align-items: center;
}

.column-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  border: 1px solid var(--orator-steel);
  border-radius: 999px;
  padding: 0.45rem 0.8rem;
  background: white;
  color: var(--orator-arsenic);
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
}

.column-chip.active {
  border-color: var(--orator-blue);
  background: #f0f9fb;
  color: #0f4f5d;
}

.column-chip input {
  margin: 0;
}

.text-button {
  border: none;
  background: transparent;
  color: var(--orator-blue);
  font-weight: 700;
  cursor: pointer;
  padding: 0.25rem;
}

.search-input,
.field-input {
  display: block;
  width: 100%;
  border-radius: 0.45rem;
  border: 1px solid var(--orator-steel);
  padding: 0.55rem 0.75rem;
  background: white;
}

.search-input {
  min-width: 280px;
}

.primary-button,
.secondary-button,
.row-button,
.queue-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.45rem;
  padding: 0.5rem 0.8rem;
  font-weight: 700;
  border: 1px solid var(--orator-steel);
  text-decoration: none;
}

.primary-button {
  background: var(--orator-blue);
  border-color: var(--orator-blue);
  color: white;
}

.secondary-button,
.row-button,
.queue-badge {
  background: white;
  color: var(--orator-arsenic);
}

.primary-button:disabled,
.secondary-button:disabled,
.row-button:disabled,
.queue-badge:disabled,
.icon-row-button:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}

.queue-badge {
  min-width: 6.5rem;
  font-size: 0.82rem;
}

.queue-badge.excluded {
  color: #9b1c1c;
  border-color: #f3c0c0;
  background: #fff7f7;
}

.lock-copy {
  margin-top: 1rem;
  color: var(--orator-graphite);
  font-weight: 600;
}

.empty-panel {
  margin-top: 1rem;
  border: 1px dashed var(--orator-steel);
  border-radius: 0.75rem;
  padding: 2rem;
  color: var(--orator-graphite);
  text-align: center;
}

.table-shell {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.9rem;
  background:
    linear-gradient(to right, rgba(8, 105, 123, 0.08), transparent 1.5rem) left center / 1.5rem 100% no-repeat,
    linear-gradient(to left, rgba(8, 105, 123, 0.08), transparent 1.5rem) right center / 1.5rem 100% no-repeat,
    white;
}

.table-scroll {
  overflow: auto;
  max-width: 100%;
  scrollbar-width: thin;
}

.table-shell.virtualized .table-scroll {
  max-height: min(70vh, 760px);
}

.participants-table {
  width: max-content;
  border-collapse: collapse;
  min-width: 100%;
}

.participants-table th,
.participants-table td {
  border-bottom: 1px solid var(--orator-smoke);
  padding: 0.85rem 0.65rem;
  text-align: left;
  vertical-align: top;
}

.participants-table th {
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--orator-graphite);
  background: var(--orator-cloud);
  position: sticky;
  top: 0;
  z-index: 3;
}

.virtual-spacer-row td {
  padding: 0;
  border-bottom: 0;
}

.participant-row.editing {
  background: var(--orator-cloud);
}

.participant-row.dragging {
  opacity: 0.55;
}

.participant-row.drag-over {
  background: #f5f3ff;
}

.index-column {
  white-space: nowrap;
}

.queue-column {
  width: 8.25rem;
  min-width: 8.25rem;
}

.drag-grip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 0.45rem;
  color: #9ca3af;
  cursor: grab;
  user-select: none;
  border: none;
  background: transparent;
  padding: 0;
  font-size: 1rem;
}

.sort-button {
  border: none;
  background: transparent;
  padding: 0;
  font: inherit;
  text-transform: inherit;
  letter-spacing: inherit;
  color: inherit;
  cursor: pointer;
}

.index-column {
  width: 4.25rem;
  min-width: 4.25rem;
  color: var(--orator-graphite);
  font-weight: 600;
}

.actions-column {
  width: 8rem;
  min-width: 8rem;
  white-space: nowrap;
}

.pronunciation-column {
  width: 15rem;
  min-width: 15rem;
}

.data-column {
  min-width: 10rem;
}

.primary-data-column {
  min-width: 15rem;
}

.sticky-left,
.sticky-right {
  position: sticky;
  background: white;
  z-index: 2;
}

.sticky-left {
  left: 0;
}

.queue-sticky {
  left: 4.25rem;
}

.sticky-right {
  right: 0;
}

.participants-table th.sticky-left,
.participants-table th.sticky-right {
  z-index: 4;
  background: var(--orator-cloud);
}

.cell-value {
  display: block;
  color: var(--orator-arsenic);
}

.truncate-cell {
  max-width: 12rem;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.data-column .truncate-cell {
  max-width: 13rem;
}

.primary-data-column .truncate-cell {
  max-width: 20rem;
}

.pronunciation-cell {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.pronunciation-pill {
  display: inline-flex;
  width: max-content;
  align-items: center;
  border-radius: 999px;
  border: 1px solid var(--orator-steel);
  padding: 0.25rem 0.55rem;
  font-size: 0.78rem;
  font-weight: 800;
  color: var(--orator-arsenic);
  background: var(--orator-cloud);
}

.pronunciation-pill.status-ready {
  color: #166534;
  border-color: #bbf7d0;
  background: #f0fdf4;
}

.pronunciation-pill.status-failed {
  color: #9b1c1c;
  border-color: #f3c0c0;
  background: #fff7f7;
}

.pronunciation-pill.status-queued,
.pronunciation-pill.status-processing {
  color: #0f4f5d;
  border-color: #b6e0e7;
  background: #f0f9fb;
}

.collection-pill {
  display: inline-flex;
  width: max-content;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.55rem;
  font-size: 0.76rem;
  font-weight: 800;
  background: var(--orator-cloud);
  color: var(--orator-arsenic);
}

.collection-needs_review,
.collection-selected_alternative {
  background: #fffbeb;
  color: #92400e;
}

.collection-approved,
.collection-confirmed_generated {
  background: #ecfdf3;
  color: #027a48;
}

.collection-rejected {
  background: #fff1f0;
  color: #b42318;
}

.check-pill {
  display: inline-flex;
  width: max-content;
  align-items: center;
  border-radius: 999px;
  padding: 0.25rem 0.55rem;
  font-size: 0.76rem;
  font-weight: 800;
  background: var(--orator-cloud);
  color: var(--orator-arsenic);
}

.check-needs_check {
  background: #fffbeb;
  color: #92400e;
}

.check-changed_at_checkin {
  background: #ecfdf3;
  color: #027a48;
}

.check-unable_to_confirm {
  background: #fff1f0;
  color: #b42318;
}

.check-checked_no_change {
  background: #eef2ff;
  color: #3538cd;
}

.pronunciation-detail {
  margin-top: 1rem;
  border-top: 1px solid var(--orator-smoke);
  padding-top: 1rem;
}

.detail-title {
  margin-bottom: 0.75rem;
  font-size: 0.92rem;
  font-weight: 800;
  color: var(--orator-navy);
}

.pronunciation-detail-grid {
  display: grid;
  gap: 0.75rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.detail-label {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--orator-graphite);
  font-size: 0.78rem;
  font-weight: 800;
  text-transform: uppercase;
}

.detail-value {
  display: block;
  color: var(--orator-arsenic);
  font-weight: 650;
  overflow-wrap: anywhere;
}

.pronunciation-actions {
  display: flex;
  align-items: center;
  gap: 0.45rem;
}

.icon-row-button {
  display: inline-flex;
  width: 2rem;
  height: 2rem;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--orator-steel);
  border-radius: 0.45rem;
  background: white;
  color: var(--orator-blue);
  font-weight: 900;
  cursor: pointer;
}

.row-icon {
  width: 1rem;
  height: 1rem;
  fill: none;
  stroke: currentColor;
  stroke-width: 2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.compact-text-button {
  font-size: 0.84rem;
  padding: 0.1rem 0;
}

.edit-shell {
  padding: 1rem;
}

.edit-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(320px, 1fr) minmax(320px, 1fr);
}

.edit-card {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  padding: 1rem;
  background: white;
}

.row-save-error {
  position: sticky;
  bottom: 0.75rem;
  z-index: 3;
  display: grid;
  gap: 0.35rem;
  margin-top: 1rem;
  border: 2px solid #b91c1c;
  border-radius: 0.75rem;
  background: #fef2f2;
  color: #7f1d1d;
  padding: 1rem;
  box-shadow: 0 14px 30px rgba(127, 29, 29, 0.18);
  font-size: 1rem;
}

.row-save-error strong {
  font-size: 1.08rem;
}

.row-save-error-detail {
  font-size: 0.9rem;
  color: #991b1b;
}

.edit-title {
  font-size: 1rem;
  font-weight: 800;
  color: var(--orator-navy);
  margin-bottom: 0.9rem;
}

.edit-form-grid,
.extra-fields-grid {
  display: grid;
  gap: 0.85rem;
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.full-span {
  grid-column: 1 / -1;
}

.field-label {
  display: block;
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--orator-arsenic);
  margin-bottom: 0.45rem;
}

.toggle-row {
  display: inline-flex;
  align-items: center;
  gap: 0.6rem;
  font-weight: 600;
  color: var(--orator-arsenic);
}

.check-request-copy {
  border: 1px solid var(--orator-smoke);
  border-radius: 0.45rem;
  background: var(--orator-cloud);
  color: var(--orator-graphite);
  padding: 0.65rem 0.75rem;
  font-weight: 700;
}

.edit-actions {
  margin-top: 1rem;
}

.edit-footer {
  margin-top: 1rem;
}

.muted-copy {
  color: var(--orator-graphite);
}

.conflict-notice {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  align-items: center;
  flex-wrap: wrap;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  background: rgba(17, 24, 39, 0.52);
}

.participant-editor-overlay {
  align-items: flex-start;
  overflow-y: auto;
}

.participant-editor-modal {
  width: min(72rem, 100%);
  max-height: calc(100vh - 3rem);
  overflow-y: auto;
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.9rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
}

.participant-editor-header {
  position: sticky;
  top: 0;
  z-index: 4;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid var(--orator-smoke);
  background: white;
}

.confirm-modal {
  width: min(34rem, 100%);
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.24);
  padding: 1.25rem;
}

.confirm-modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.modal-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 1.25rem;
}

.notice {
  margin-bottom: 1rem;
  padding: 0.9rem 1rem;
  border-radius: 0.65rem;
  font-weight: 600;
}

.notice.error {
  background: #fef2f2;
  color: #b91c1c;
  border: 1px solid #fecaca;
}

.notice.success {
  background: #f0fdf4;
  color: #166534;
  border: 1px solid #bbf7d0;
}

.notice.warning {
  background: #fffbeb;
  color: #92400e;
  border: 1px solid #fcd34d;
}

@media (max-width: 960px) {
  .event-layout {
    width: auto;
    flex-direction: column;
    padding: 0 1rem 2.5rem;
  }

  :deep(.sidebar) {
    width: 100%;
    flex-basis: 100%;
  }

  .page-header {
    flex-direction: column;
  }

  .search-input {
    min-width: 100%;
  }

  .edit-grid,
  .edit-form-grid,
  .extra-fields-grid {
    grid-template-columns: 1fr;
  }

  .modal-overlay {
    padding: 0.75rem;
  }

  .participant-editor-modal {
    max-height: calc(100vh - 1.5rem);
  }

  .participant-editor-header {
    flex-direction: column;
  }
}
</style>
